import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Shield, Package, DollarSign, Users, Trash2, CheckCircle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [completeConfirm, setCompleteConfirm] = useState(null);
  const [resetRevenueConfirm, setResetRevenueConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total_count: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchOrders(token, currentPage);
    fetchRevenue(token);
  }, [navigate, currentPage]);

  const fetchOrders = async (token, page = 1) => {
    try {
      setError(null);
      const response = await axios.get(`${BACKEND_URL}/api/orders?page=${page}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      // Filter out auto-deleted completed orders
      const activeOrders = response.data.orders.filter(order => {
        if (order.status === 'Completed' && order.auto_delete_at) {
          return new Date(order.auto_delete_at) > new Date();
        }
        return true;
      });
      setOrders(activeOrders);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenue = async (token) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/revenue`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      setTotalRevenue(response.data.total_revenue);
    } catch (error) {
      console.error('Error fetching revenue:', error);
    } finally {
      setRevenueLoading(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    setDeleting(true);
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${BACKEND_URL}/api/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOrders(orders.filter(o => o.order_id !== orderId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting order:', error);
      alert(error.response?.data?.detail || 'Failed to delete order');
    } finally {
      setDeleting(false);
    }
  };

  const handleMarkCompleted = async (orderId) => {
    setCompleting(true);
    const token = localStorage.getItem('token');
    
    try {
      await axios.post(`${BACKEND_URL}/api/orders/${orderId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update order in local state
      setOrders(orders.map(o => 
        o.order_id === orderId 
          ? { ...o, status: 'Completed', completed_at: new Date().toISOString() }
          : o
      ));
      setCompleteConfirm(null);
      
      // Revenue doesn't change when marking completed
    } catch (error) {
      console.error('Error marking order as completed:', error);
      alert(error.response?.data?.detail || 'Failed to mark order as completed');
    } finally {
      setCompleting(false);
    }
  };

  const handleResetRevenue = async () => {
    const token = localStorage.getItem('token');
    
    try {
      await axios.post(`${BACKEND_URL}/api/revenue/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTotalRevenue(0);
      setResetRevenueConfirm(false);
      alert('Total revenue has been reset to $0.00');
    } catch (error) {
      console.error('Error resetting revenue:', error);
      alert(error.response?.data?.detail || 'Failed to reset revenue');
    }
  };

  const handleCleanupSpam = async () => {
    const token = localStorage.getItem('token');
    
    if (!window.confirm('This will delete excessive orders from spam accounts. Continue?')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${BACKEND_URL}/api/orders/cleanup-spam`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert(`Cleaned up ${response.data.deleted_count} spam orders`);
      // Refresh orders
      fetchOrders(token);
    } catch (error) {
      console.error('Error cleaning up spam:', error);
      alert(error.response?.data?.detail || 'Failed to cleanup spam orders');
    }
  };

  const handleDeleteAllOrders = async () => {
    const token = localStorage.getItem('token');
    
    if (!window.confirm('⚠️ EMERGENCY: This will DELETE ALL ORDERS and reset revenue to $0. Are you sure?')) {
      return;
    }
    
    if (!window.confirm('⚠️ FINAL WARNING: This action CANNOT be undone. Type OK to confirm you want to delete ALL orders.')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${BACKEND_URL}/api/orders/delete-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert(`✅ DELETED ${response.data.deleted_count} orders. Revenue reset to $0.00`);
      // Refresh orders and revenue
      fetchOrders(token);
      fetchRevenue(token);
    } catch (error) {
      console.error('Error deleting all orders:', error);
      alert(error.response?.data?.detail || 'Failed to delete orders');
    }
  };

  const getStatusColor = (status) => {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case 'completed': return 'text-green-500 border-green-500/30 bg-green-900/20';
      case 'pending': return 'text-yellow-500 border-yellow-500/30 bg-yellow-900/20';
      case 'paid': return 'text-blue-500 border-blue-500/30 bg-blue-900/20';
      default: return 'text-gray-500 border-gray-500/30 bg-gray-900/20';
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    return order.status?.toLowerCase() === filter;
  });

  // Calculate stats (case-insensitive)
  const stats = {
    totalOrders: orders.length,
    totalRevenue: totalRevenue,
    pendingOrders: orders.filter(o => o.status?.toLowerCase() === 'pending').length,
    paidOrders: orders.filter(o => o.status?.toLowerCase() === 'paid').length,
    completedOrders: orders.filter(o => o.status?.toLowerCase() === 'completed').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">{error}</div>
          <button 
            onClick={() => window.location.reload()}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 uppercase tracking-wider"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-content bg-[#050505] min-h-screen">
      <Navbar />
      
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-12 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Shield className="text-red-500" size={40} />
              <div>
                <h1 className="text-4xl md:text-5xl font-black uppercase">
                  <span className="text-red-500">ADMIN</span> DASHBOARD
                </h1>
                <p className="text-gray-400 font-['Rajdhani'] text-lg">Manage orders and revenue</p>
              </div>
            </div>
          </div>

          {/* Revenue Display */}
          <div className="mb-8 bg-gradient-to-r from-red-900/20 to-red-800/10 border border-red-500/30 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">Total Revenue (Paid Orders Only)</p>
                <p className="text-5xl font-black text-white">
                  {revenueLoading ? 'Loading...' : `$${totalRevenue.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}
                </p>
                <p className="text-xs text-gray-500 mt-2">Revenue increases only when orders are marked as Paid</p>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => setResetRevenueConfirm(true)}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 uppercase tracking-wider transition-all text-xs"
                  title="Reset Total Revenue to $0"
                >
                  <RefreshCw size={16} />
                  Reset Revenue
                </button>
                <button
                  onClick={handleCleanupSpam}
                  className="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 uppercase tracking-wider transition-all text-xs"
                  title="Delete spam orders"
                >
                  <Trash2 size={16} />
                  Cleanup Spam
                </button>
                <button
                  onClick={handleDeleteAllOrders}
                  className="flex items-center gap-2 bg-red-800 hover:bg-red-900 text-white px-4 py-2 uppercase tracking-wider transition-all text-xs border-2 border-red-500"
                  title="EMERGENCY: Delete ALL orders"
                >
                  <Trash2 size={16} />
                  DELETE ALL
                </button>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <div className="flex items-center justify-between mb-2">
                <Package className="text-red-500" size={24} />
              </div>
              <p className="text-3xl font-black text-white">{stats.totalOrders}</p>
              <p className="text-gray-400 text-sm uppercase tracking-wider">Total Orders</p>
            </div>

            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="text-yellow-500" size={24} />
              </div>
              <p className="text-3xl font-black text-white">{stats.pendingOrders}</p>
              <p className="text-gray-400 text-sm uppercase tracking-wider">Pending Orders</p>
            </div>

            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="text-blue-500" size={24} />
              </div>
              <p className="text-3xl font-black text-white">{stats.paidOrders}</p>
              <p className="text-gray-400 text-sm uppercase tracking-wider">Paid Orders</p>
            </div>

            <div className="bg-[#0A0A0A] border border-white/10 p-6">
              <div className="flex items-center justify-between mb-2">
                <Users className="text-green-500" size={24} />
              </div>
              <p className="text-3xl font-black text-white">{stats.completedOrders}</p>
              <p className="text-gray-400 text-sm uppercase tracking-wider">Completed Orders</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-8">
            {['all', 'pending', 'paid', 'completed'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-6 py-2 uppercase tracking-wider border transition-all ${
                  filter === status
                    ? 'bg-red-600 border-red-600 text-white'
                    : 'bg-transparent border-white/10 text-gray-400 hover:border-red-500/50'
                }`}
              >
                {status}
              </button>
            ))}
          </div>

          {/* Orders Table */}
          <div className="bg-[#0A0A0A] border border-white/10 overflow-x-auto">
            <table className="w-full" data-testid="orders-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-gray-400 uppercase tracking-wider text-sm">Order</th>
                  <th className="text-left p-4 text-gray-400 uppercase tracking-wider text-sm">Customer</th>
                  <th className="text-left p-4 text-gray-400 uppercase tracking-wider text-sm">Gamer Tag</th>
                  <th className="text-left p-4 text-gray-400 uppercase tracking-wider text-sm">Platform</th>
                  <th className="text-left p-4 text-gray-400 uppercase tracking-wider text-sm">Price</th>
                  <th className="text-left p-4 text-gray-400 uppercase tracking-wider text-sm">Status</th>
                  <th className="text-left p-4 text-gray-400 uppercase tracking-wider text-sm">Date</th>
                  <th className="text-left p-4 text-gray-400 uppercase tracking-wider text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order, index) => (
                  <tr
                    key={index}
                    className="border-b border-white/10 hover:bg-white/5 transition-colors"
                    data-testid={`admin-order-${index}`}
                  >
                    <td className="p-4 text-white font-['Rajdhani']">{order.product_name}</td>
                    <td className="p-4 text-gray-400 text-sm">{order.email || 'N/A'}</td>
                    <td className="p-4 text-gray-400">{order.gamer_tag}</td>
                    <td className="p-4 text-gray-400">{order.platform}</td>
                    <td className="p-4 text-red-500 font-bold">{order.product_price}</td>
                    <td className="p-4">
                      <span className={`px-3 py-1 text-xs uppercase tracking-wider border ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="p-4 text-gray-400 text-sm">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2 items-center">
                        {/* Complete Order button - only for Paid orders (case-insensitive) */}
                        {order.status?.toLowerCase() === 'paid' && (
                          <button
                            onClick={() => setCompleteConfirm(order)}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-xs uppercase tracking-wider transition-all flex items-center gap-1"
                            title="Mark as Completed"
                          >
                            <CheckCircle size={14} />
                            Complete
                          </button>
                        )}
                        
                        {/* Delete button - available for all orders */}
                        <button
                          onClick={() => setDeleteConfirm(order)}
                          className="text-red-500 hover:text-red-400 transition-colors"
                          title="Delete Order"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-6 bg-black/40 border border-white/10 p-4">
              <div className="text-gray-400 text-sm">
                Showing page {pagination.page} of {pagination.total_pages} ({pagination.total_count} total orders)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={!pagination.has_prev}
                  className={`flex items-center gap-1 px-4 py-2 uppercase tracking-wider text-sm transition-all ${
                    pagination.has_prev 
                      ? 'bg-red-600 hover:bg-red-700 text-white' 
                      : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={!pagination.has_next}
                  className={`flex items-center gap-1 px-4 py-2 uppercase tracking-wider text-sm transition-all ${
                    pagination.has_next 
                      ? 'bg-red-600 hover:bg-red-700 text-white' 
                      : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {filteredOrders.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              No orders found for the selected filter.
            </div>
          )}

          {/* Mark as Completed Confirmation Modal */}
          {completeConfirm && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4">
              <div className="bg-[#0A0A0A] border border-green-500/50 p-8 max-w-md w-full">
                <h3 className="text-2xl font-bold text-white mb-4 uppercase">Mark as Completed</h3>
                <p className="text-gray-400 mb-6">
                  Are you sure you want to mark this order as completed?
                </p>
                <div className="bg-black/40 border border-white/10 p-4 mb-6 text-sm">
                  <p className="text-gray-400"><span className="text-white">Product:</span> {completeConfirm.product_name}</p>
                  <p className="text-gray-400"><span className="text-white">Customer:</span> {completeConfirm.email || 'N/A'}</p>
                  <p className="text-gray-400"><span className="text-white">Status:</span> {completeConfirm.status}</p>
                  <p className="text-gray-400"><span className="text-white">Price:</span> {completeConfirm.product_price}</p>
                </div>
                <p className="text-yellow-500 text-xs mb-6 bg-yellow-900/20 border border-yellow-500/30 p-3">
                  ℹ️ This order will auto-delete from the dashboard after 30 days. Revenue will NOT be affected.
                </p>
                <div className="flex gap-4">
                  <button
                    onClick={() => handleMarkCompleted(completeConfirm.order_id)}
                    disabled={completing}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 uppercase tracking-wider transition-all disabled:opacity-50"
                  >
                    {completing ? 'MARKING...' : 'MARK COMPLETED'}
                  </button>
                  <button
                    onClick={() => setCompleteConfirm(null)}
                    disabled={completing}
                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 uppercase tracking-wider transition-all disabled:opacity-50"
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {deleteConfirm && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4">
              <div className="bg-[#0A0A0A] border border-red-500/50 p-8 max-w-md w-full">
                <h3 className="text-2xl font-bold text-white mb-4 uppercase">CONFIRM DELETE</h3>
                <p className="text-gray-400 mb-6">
                  Are you sure you want to delete this order?
                </p>
                <div className="bg-black/40 border border-white/10 p-4 mb-6 text-sm">
                  <p className="text-gray-400"><span className="text-white">Product:</span> {deleteConfirm.product_name}</p>
                  <p className="text-gray-400"><span className="text-white">Customer:</span> {deleteConfirm.email || 'N/A'}</p>
                  <p className="text-gray-400"><span className="text-white">Status:</span> {deleteConfirm.status}</p>
                </div>
                <p className="text-yellow-500 text-xs mb-6 bg-yellow-900/20 border border-yellow-500/30 p-3">
                  ⚠️ Deleting orders does NOT affect total revenue.
                </p>
                <div className="flex gap-4">
                  <button
                    onClick={() => handleDeleteOrder(deleteConfirm.order_id)}
                    disabled={deleting}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 uppercase tracking-wider transition-all disabled:opacity-50"
                  >
                    {deleting ? 'DELETING...' : 'DELETE'}
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    disabled={deleting}
                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 uppercase tracking-wider transition-all disabled:opacity-50"
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Reset Revenue Confirmation Modal */}
          {resetRevenueConfirm && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4">
              <div className="bg-[#0A0A0A] border border-red-500/50 p-8 max-w-md w-full">
                <h3 className="text-2xl font-bold text-white mb-4 uppercase">⚠️ RESET REVENUE</h3>
                <p className="text-gray-400 mb-6">
                  Are you sure you want to reset total revenue to $0.00?
                </p>
                <p className="text-red-500 text-sm mb-6 bg-red-900/20 border border-red-500/30 p-4">
                  ⚠️ <strong>WARNING:</strong> This action will reset the revenue counter to $0.00. This does NOT affect order records or order statuses.
                </p>
                <div className="flex gap-4">
                  <button
                    onClick={handleResetRevenue}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 uppercase tracking-wider transition-all"
                  >
                    RESET TO $0
                  </button>
                  <button
                    onClick={() => setResetRevenueConfirm(false)}
                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 uppercase tracking-wider transition-all"
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdminPage;
